import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ReactComponent as Logo } from "../assets/Logo.svg";
import AppEventDetailsEn from "../assets/en/nightly-features-details-screen.png";
import AppEventDetailsHe from "../assets/he/nightly-features-details-screen-he.png";
import TicketGlowImagePng from "../assets/tickets-glow.png";
import { device } from "../breakpoints";
import LangSwitcher from "../components/langswitcher/LangSwitcher";
import Seo from "../components/Seo";
import StoreButtons from "../components/StoreButtons";
import { NIGHTLY_APP_SCHEME } from "../constants/launch";
import { Switcher } from "../components/langswitcher/logic.jsx";
import {
  detectStorePlatform,
  getStoreUrlForPlatform,
  STORE_PLATFORM,
} from "../utils/deviceStore";
import { trackAppDownloadIntent } from "../utils/pixels";

const STORE_REDIRECT_DELAY_MS = 1800;
const STORE_KEY_BY_PLATFORM = {
  [STORE_PLATFORM.IOS]: "appStore",
  [STORE_PLATFORM.ANDROID]: "googlePlay",
};

function getCurrentStorePlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return STORE_PLATFORM.OTHER;
  }

  return detectStorePlatform({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  });
}

function buildAppEventUrl(eventId) {
  if (!eventId) return `${NIGHTLY_APP_SCHEME}://`;
  return `${NIGHTLY_APP_SCHEME}://event/${encodeURIComponent(eventId)}/details`;
}

function EventShareFallback() {
  const { eventId } = useParams();
  const { t, i18n } = useTranslation();
  Switcher();

  const currentLanguage =
    i18n.resolvedLanguage === "he" || i18n.language === "he" ? "he" : "en";
  const isRtl = currentLanguage === "he";
  const previewImage = isRtl ? AppEventDetailsHe : AppEventDetailsEn;
  const autoRedirectTrackedRef = useRef(false);
  const [storePlatform] = useState(getCurrentStorePlatform);
  const storeKey = STORE_KEY_BY_PLATFORM[storePlatform];
  const storeUrl = useMemo(
    () => getStoreUrlForPlatform(storePlatform),
    [storePlatform]
  );
  const appEventUrl = useMemo(() => buildAppEventUrl(eventId), [eventId]);
  const storeName =
    storePlatform === STORE_PLATFORM.IOS
      ? t("event_fallback.app_store")
      : t("event_fallback.google_play");

  useEffect(() => {
    if (!storeUrl) return undefined;

    if (!autoRedirectTrackedRef.current) {
      autoRedirectTrackedRef.current = true;
      trackAppDownloadIntent({
        store: storeKey,
        placement: "event_fallback_auto_redirect",
        destinationUrl: storeUrl,
        eventId,
        intentType: "auto_store_redirect",
      });
    }

    const redirectTimer = window.setTimeout(() => {
      window.location.replace(storeUrl);
    }, STORE_REDIRECT_DELAY_MS);

    return () => window.clearTimeout(redirectTimer);
  }, [eventId, storeKey, storeUrl]);

  return (
    <Page dir={isRtl ? "rtl" : "ltr"}>
      <Seo
        title={t("event_fallback.seo_title")}
        description={t("event_fallback.seo_description")}
        path={eventId ? `/event/${eventId}/details` : "/event"}
        robots="noindex, nofollow"
      />
      <Header>
        <LogoLink href="/" aria-label="Nightly">
          <Logo />
        </LogoLink>
        <LangSwitcher />
      </Header>

      <Main>
        <Content>
          <StatusPill>
            <PulseDot aria-hidden="true" />
            {storeUrl
              ? t("event_fallback.redirecting_status", { storeName })
              : t("event_fallback.desktop_status")}
          </StatusPill>

          <Title>{t("event_fallback.title")}</Title>
          <Subtitle>
            {storeUrl
              ? t("event_fallback.mobile_subtitle", { storeName })
              : t("event_fallback.desktop_subtitle")}
          </Subtitle>

          <Actions aria-label={t("event_fallback.store_buttons_label")}>
            <StoreButtons
              variant="hero"
              align="start"
              mobileAlign="center"
              trackingPlacement="event_fallback_manual_click"
              store={
                storePlatform === STORE_PLATFORM.IOS
                  ? "appStore"
                  : storePlatform === STORE_PLATFORM.ANDROID
                  ? "googlePlay"
                  : "all"
              }
            />
            <OpenAppButton href={appEventUrl}>
              {t("event_fallback.open_app_button")}
            </OpenAppButton>
          </Actions>

          <SecondaryText>
            {storeUrl
              ? t("event_fallback.manual_store_hint")
              : t("event_fallback.desktop_hint")}
          </SecondaryText>
        </Content>

        <Preview aria-hidden="true">
          <PreviewGlow src={TicketGlowImagePng} alt="" />
          <PreviewImage src={previewImage} alt="" />
        </Preview>
      </Main>
    </Page>
  );
}

export default EventShareFallback;

const Page = styled.div`
  min-height: 100vh;
  overflow-x: hidden;
  color: #f8f8ef;
  background: linear-gradient(135deg, #070707 0%, #171712 54%, #10100d 100%);
`;

const Header = styled.header`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;
  padding: 30px 0 0;
  direction: ltr;

  .container {
    margin: 0;
  }

  .dropdown {
    margin: 0;
  }
`;

const LogoLink = styled.a`
  display: inline-flex;
  align-items: center;
  color: #ffffff;

  svg {
    width: 116px;
    height: auto;
  }
`;

const Main = styled.main`
  position: relative;
  z-index: 1;
  display: grid;
  width: min(1120px, calc(100% - 40px));
  min-height: calc(100vh - 92px);
  margin: 0 auto;
  padding: 40px 0 70px;
  align-items: center;
  gap: 44px;

  @media ${device.pc} {
    grid-template-columns: minmax(0, 1.02fr) minmax(300px, 0.78fr);
    padding-top: 20px;
  }
`;

const Content = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-self: start;
  width: 100%;
  max-width: 620px;
  text-align: start;

  [dir="rtl"] & {
    direction: rtl;
  }
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  margin-bottom: 24px;
  padding: 8px 14px;
  border: 1px solid rgba(233, 233, 40, 0.32);
  border-radius: 999px;
  color: #e9e928;
  background: rgba(233, 233, 40, 0.08);
  font-size: 0.9rem;
  font-weight: 600;
  direction: inherit;
  text-align: start;
  unicode-bidi: isolate;
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(0.82);
    opacity: 0.62;
  }

  50% {
    transform: scale(1);
    opacity: 1;
  }
`;

const PulseDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #e9e928;
  box-shadow: 0 0 16px rgba(233, 233, 40, 0.62);
  animation: ${pulse} 1.2s ease-in-out infinite;
`;

const Title = styled.h1`
  max-width: 680px;
  margin: 0;
  font-size: 2.35rem;
  line-height: 0.98;
  font-weight: 800;
  letter-spacing: 0;

  @media ${device.tablet} {
    font-size: 3.8rem;
  }

  @media ${device.pc} {
    font-size: 5.25rem;
  }
`;

const Subtitle = styled.p`
  max-width: 560px;
  margin: 24px 0 0;
  color: rgba(248, 248, 239, 0.78);
  font-size: 1.08rem;
  line-height: 1.65;

  @media ${device.pc} {
    font-size: 1.2rem;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 22px;
  width: 100%;
  margin-top: 34px;

  [dir="rtl"] & {
    direction: rtl;
  }

  @media (max-width: 520px) {
    align-items: center;
    gap: 24px;

    [dir="rtl"] & {
      align-items: center;
    }
  }
`;

const OpenAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 190px;
  padding: 0 22px;
  border: 1px solid rgba(248, 248, 239, 0.2);
  border-radius: 999px;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.07);
  text-decoration: none;
  font-size: 0.98rem;
  font-weight: 700;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(233, 233, 40, 0.45);
    background: rgba(233, 233, 40, 0.1);
  }

  @media (max-width: 520px) {
    min-height: 42px;
    min-width: 154px;
    width: auto;
    padding: 0 18px;
    font-size: 0.88rem;
  }
`;

const SecondaryText = styled.p`
  max-width: 520px;
  margin-top: 18px;
  color: rgba(248, 248, 239, 0.52);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Preview = styled.aside`
  display: none;
  justify-content: center;
  align-items: center;
  position: relative;

  @media ${device.pc} {
    display: flex;
  }
`;

const PreviewGlow = styled.img`
  position: absolute;
  left: 50%;
  top: 52%;
  z-index: 0;
  width: min(560px, 165%);
  height: auto;
  opacity: 0.42;
  pointer-events: none;
  transform: translate(-50%, -50%);
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(3deg);
  }

  50% {
    transform: translateY(-12px) rotate(3deg);
  }
`;

const PreviewImage = styled.img`
  display: block;
  position: relative;
  z-index: 1;
  width: min(330px, 100%);
  height: auto;
  filter: drop-shadow(0 34px 48px rgba(0, 0, 0, 0.46));
  animation: ${float} 5.6s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
